import React from 'react';
import { AnimatedCurrency } from '@hub/components/animated-currency';
import { LabelWithTooltip } from '@hub/features/account-dashboard/components/label-with-tooltip';
import { Stack, Typography, useTheme } from '@mui/material';
import type { TypographyProps } from '@mui/material/Typography';
import { useBreakpoints } from '@monorepo/nova-ui';
import { useResponsiveFontSize } from './hooks';

interface MetricProps {
  label: string;
  tooltip: string;
  value: React.ReactNode;
  numericValue?: number; // Add raw numeric value for animation
  primary?: boolean;
  valueProps?: TypographyProps;
  compact?: boolean;
  minWidth?: number;
}

export const Metric: React.FC<MetricProps> = ({
  label,
  tooltip,
  value,
  numericValue,
  primary = false,
  valueProps,
  compact = false,
  minWidth,
}) => {
  const theme = useTheme();
  const { isMobile } = useBreakpoints();
  const { responsiveFontSizes, currentFontSize } =
    useResponsiveFontSize(compact);

  const defaultStyles: TypographyProps = {
    fontWeight: 400,
    fontStyle: compact ? 'normal' : 'italic',
    sx: (theme) => ({
      fontSize: responsiveFontSizes,
      color: theme.palette.text.second,
      letterSpacing: '-0.03em',
    }),
  };

  const primaryStyles: TypographyProps = {
    fontWeight: 700,
    fontStyle: compact ? 'normal' : 'italic',
    sx: (theme) => ({
      fontSize: responsiveFontSizes,
      color: compact
        ? theme.palette.primary.main
        : theme.palette.secondaryInvert.main,
      letterSpacing: '-1em',
    }),
  };

  const styles = primary ? primaryStyles : defaultStyles;

  // Check if we have a numeric value for formatting
  const shouldFormatNumeric = typeof numericValue === 'number';

  const renderValue = () => {
    if (shouldFormatNumeric && numericValue !== undefined) {
      // Get theme color for the counter
      const textColor = primary
        ? compact
          ? theme.palette.primary.main
          : theme.palette.secondaryInvert.main
        : theme.palette.text.second;

      return (
        <AnimatedCurrency
          value={numericValue}
          fontSize={currentFontSize}
          textColor={textColor}
          fontWeight={primary ? 'bold' : 'normal'}
          compact={compact}
          currencySymbol="$"
          showAnimation={true}
          fontStyle={compact ? 'normal' : 'italic'}
          showDecimals={!isMobile}
          letterSpacing="-0.03em"
        />
      );
    }

    return (
      <Typography variant="h2" {...styles} {...valueProps}>
        {value}
      </Typography>
    );
  };

  return (
    <Stack gap={1} minWidth={minWidth}>
      <LabelWithTooltip
        label={label}
        tooltip={tooltip}
        showIcon={!compact}
        textSize={isMobile ? 10 : 12}
      />
      {renderValue()}
    </Stack>
  );
};
